/**
 * Claim API client (D19 + D23 preview flow).
 *
 * Auth (2026-07-22 SECURITY): every call sends `Authorization: Bearer
 * <token>`, where `token` is a Clerk session token the calling component
 * fetches via `useAuth().getToken()`. The backend verifies it with
 * get_current_user. This replaced the legacy `x-mio-user-id` header,
 * which the API only checked for presence (any value passed), letting a
 * stranger drive the retailer-email flow. Matches the vault.ts pattern.
 */

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return process.env.API_INTERNAL_URL ?? process.env.API_URL ?? "http://localhost:8000";
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export interface ClaimRequestBody {
  target_retailer_slug: string;
  target_retailer_domain?: string;
  target_product_url: string;
  target_product_name: string;
  target_sku?: string;
  target_sticker_price_aud: string;
  requested_price_aud: string;
  competitor_retailer_slug: string;
  competitor_product_url: string;
  competitor_price_aud: string;
  competitor_in_stock: boolean;
  competitor_fetched_at?: string;
  matching_model_number?: string;
  sku_match_verdict: boolean;
  user_name: string;
  user_email: string;
  country_code?: string;
  // D23: set true after the user has seen and confirmed the
  // show-policy modal. Required for Tier 2 Q3/Q4 retailers + D31
  // equivalent_variant claims; ignored for strict_match Tier 1 + Q5.
  user_confirmed?: boolean;
  // D31: equivalence kind. "strict_match" is the default (identical
  // model numbers); "equivalent_variant" must be an explicit user
  // opt-in after seeing the honest-probability decision surface.
  equivalence_kind?: "strict_match" | "equivalent_variant";
  // D26: alternative-competitor candidates (cheapest-first). On a
  // fixable refusal the retry pipeline pops the next entry as proof.
  available_competitors?: ClaimCompetitor[];
  // D2: link to Mio's frozen advertised-price evidence page (the saved
  // comparison snapshot) cited in the email so the retailer can verify
  // the competitor's advertised price.
  mio_evidence_url?: string;
}

export interface ClaimCompetitor {
  retailer_slug: string;
  product_url: string;
  price_aud: string;
  in_stock: boolean;
  matching_model_number?: string | null;
}

/**
 * D23 preview status. Drives which modal the UI renders.
 */
export type PreviewStatus =
  | "tier_1_has_policy_auto"
  | "tier_1_has_policy_manual"
  | "tier_1_no_policy"
  | "tier_1_policy_mismatch"
  | "tier_1_equivalent_variant_confirm"
  | "tier_2_q1_not_supported"
  | "tier_2_q2_evidence_pack"
  | "tier_2_q3_confirm_each_field"
  | "tier_2_q4_show_policy_and_confirm"
  | "tier_2_q5_auto_submit"
  | "tier_2_no_channel"
  | "tos_blocked";

export interface BundledCompetitor {
  retailer_slug: string;
  product_url: string | null;
  price_aud: string | null;
  in_stock: boolean | null;
  matching_model_number: string | null;
}

export interface ClaimPreviewResponse {
  status: PreviewStatus;
  confidence: number;
  policy: Record<string, unknown> | null;
  composed_subject: string | null;
  composed_body: string | null;
  // Clickable HTML rendering of the email (signed-off template; the
  // "your Mio price comparison" link points to Mio). Rendered by the
  // modal when present, else composed_body (plain text) is shown.
  // Optional so older/mock payloads that pre-date it still type-check.
  composed_body_html?: string | null;
  // 2026-07-12: the raw recipient email is no longer sent to the client
  // (always null). The modal shows `to_display` (e.g. "The Mobileciti team").
  to_address: string | null;
  to_display: string | null;
  preferred_channel: string | null;
  tos_forbids_automation: boolean;
  requires_user_confirmation: boolean;
  reason: string | null;
  retailer_display_name: string;
  // D30: reply prediction. Float in [0, 1] when the path is
  // submittable; null when the path can't submit at all.
  predicted_approval_likelihood: number | null;
  // D30: which signal produced the likelihood. UI softens framing
  // on weak bases (baseline_no_history, llm_estimate).
  prediction_basis: string | null;
  // D30: the 2-3 cheapest acceptable competitors Mio will cite as
  // proof in the composed email. Index 0 is always the primary.
  bundled_competitors: BundledCompetitor[];
  // D31: equivalence kind echoed back. Drives modal copy: variant
  // claims show the spec's honest-probability framing.
  equivalence_kind: "strict_match" | "equivalent_variant";
  // 2026-07-12: mirrors the backend generic-best-offer flag. When true the
  // modal header (title + badge) drops the match/beat framing to match the
  // generic "best offer" email body. Optional so pre-existing payloads
  // still type-check.
  generic_best_offer?: boolean;
}

export interface ValidationFailureView {
  check: string;
  reason: string;
}

export interface ClaimResponse {
  claim_id: string;
  transaction_id: string;
  status: string;
  transport_used: string | null;
  sent_at: string | null;
  external_message_id: string | null;
  error: string | null;
  validator_passed: boolean;
  validator_failures: ValidationFailureView[];
  // 2026-06-27: at-submit re-verify refusal context. Both null except on
  // a MANUAL_REQUIRED re-verify refusal (error is one of competitor_oos /
  // competitor_now_higher / target_oos / target_now_lower). The modal uses
  // these to render a plain-English "didn't send" message instead of the
  // raw refusal code. reverify_competitor_price_aud is the competitor's
  // live re-checked price; competitor_display_name is the cited competitor
  // prettified ("bing-lee" -> "Bing Lee").
  reverify_competitor_price_aud?: string | null;
  competitor_display_name?: string | null;
}

export class ClaimsApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export async function submitClaim(
  body: ClaimRequestBody,
  token: string,
): Promise<ClaimResponse> {
  const res = await fetch(`${apiBaseUrl()}/api/claims`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ClaimsApiError(
      res.status,
      detail,
      `POST /api/claims returned ${res.status}`,
    );
  }
  return (await res.json()) as ClaimResponse;
}

/**
 * D23 preview. POSTs to /api/claims/preview to get the show-policy
 * payload BEFORE the user authorises a submission. Does not record
 * an audit row, mints no transaction_id, sends nothing.
 *
 * The frontend uses the returned `status` to pick which modal to
 * render: Tier 1 auto / manual / no-policy, Tier 2 Q1-Q5, ToS-blocked.
 * For statuses with `requires_user_confirmation=true` (Q3/Q4), the
 * confirm action POSTs to /api/claims with `user_confirmed: true`.
 */
export async function previewClaim(
  body: ClaimRequestBody,
  token: string,
): Promise<ClaimPreviewResponse> {
  const res = await fetch(`${apiBaseUrl()}/api/claims/preview`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ClaimsApiError(
      res.status,
      detail,
      `POST /api/claims/preview returned ${res.status}`,
    );
  }
  return (await res.json()) as ClaimPreviewResponse;
}

// ---------- read endpoints (D13/D15: My requests) ---------------------

/** Lifecycle status of a claim, mirroring api ClaimStatus. */
export type ClaimStatusValue =
  | "composed"
  | "validator_failed"
  | "sent"
  | "send_failed"
  | "approved"
  | "refused"
  | "needs_more_info"
  | "unparseable"
  | "needs_review"
  | "manual_required"
  | "abandoned";

/**
 * One turn in a claim's retailer conversation, assembled server-side from
 * claim.composed_body (Mio's first email) + the audit log. sender is "mio"
 * (Mio on the user's behalf), "retailer" (an inbound reply), or "user" (the
 * user's own reply, sent through Mio). body is null only for a Mio auto
 * follow-up recorded before its text was persisted.
 */
export interface ThreadMessage {
  sender: "mio" | "retailer" | "user";
  sent_at: string;
  body: string | null;
}

export interface ClaimListItem {
  claim_id: string;
  transaction_id: string;
  status: ClaimStatusValue | string;
  target_retailer_slug: string;
  target_product_name: string | null;
  target_product_url: string;
  target_sku: string | null;
  requested_price_aud: string;
  competitor_retailer_slug: string;
  competitor_price_aud: string;
  equivalence_kind: string;
  created_at: string | null;
  sent_at: string | null;
  outcome_at: string | null;
  matched_price_aud: string | null;
  redemption_method: string | null;
  redemption_instructions: string | null;
  summary: string | null;
  target_sticker_price_aud: string | null;
  refusal_reason: string | null;
  needs_more_info_reason: string | null;
  // Raw retailer reply text, flattened so the card shows the reply
  // inline without a per-row detail fetch. Null until a reply arrives.
  raw_reply: string | null;
  is_retry: boolean;
  // Full chronological conversation (Mio's first email, every retailer
  // reply, the user's replies, Mio's follow-ups). Replaces the single
  // raw_reply surface so second/third retailer replies are no longer lost.
  thread: ThreadMessage[];
  // Broadcast grouping fields (Day 2; returned by _to_list_item for every
  // claim, null on single non-broadcast claims). The grouped
  // /api/broadcasts list + the rebuilt requests screen rely on them.
  broadcast_id?: string | null;
  broadcast_expires_at?: string | null;
  accepted_at?: string | null;
  // The shop's True-Final-Price breakdown captured at ask time. The
  // requests screen rebuilds each offer's breakdown from it, swapping the
  // agreed price (matched_price_aud) in as the base layer.
  price_components_snapshot?: SnapshotLayer[] | null;
}

/** One layer of a shop's captured True Final Price breakdown - a subset of
 * the comparison PriceComponent, stored as opaque JSONB and returned
 * verbatim. Only the fields the requests breakdown reads are typed. */
export interface SnapshotLayer {
  name: string;
  amount: string;
  description?: string;
  confidence?: string;
  source?: string | null;
  verified_at?: string | null;
}

export interface ClaimListResponse {
  claims: ClaimListItem[];
}

export interface ClaimDetail extends ClaimListItem {
  composed_subject: string | null;
  composed_body: string | null;
  mio_evidence_url: string | null;
  transport_used: string | null;
  confidence: number | null;
  redemption_reference: string | null;
  gated_low_confidence: boolean;
  tentative_outcome: string | null;
  refusal_category: string | null;
  retry_count: number;
}

export async function listClaims(token: string): Promise<ClaimListItem[]> {
  const res = await fetch(`${apiBaseUrl()}/api/claims`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new ClaimsApiError(
      res.status,
      null,
      `GET /api/claims returned ${res.status}`,
    );
  }
  const body = (await res.json()) as ClaimListResponse;
  return body.claims;
}

export async function getClaim(
  claimId: string,
  token: string,
): Promise<ClaimDetail> {
  const res = await fetch(`${apiBaseUrl()}/api/claims/${claimId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new ClaimsApiError(
      res.status,
      null,
      `GET /api/claims/${claimId} returned ${res.status}`,
    );
  }
  return (await res.json()) as ClaimDetail;
}

export interface ClaimActionResponse {
  ok: boolean;
  status: string;
}

/**
 * Send the user's typed reply to the retailer, in the same email thread,
 * via Mio (POST /api/claims/{id}/reply). Valid only for claims with a
 * live retailer thread (agreed / declined / following-up / needs-review /
 * reply-needs-a-look); the API returns 409 otherwise.
 */
export async function sendClaimReply(
  claimId: string,
  token: string,
  text: string,
): Promise<ClaimActionResponse> {
  const res = await fetch(`${apiBaseUrl()}/api/claims/${claimId}/reply`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ClaimsApiError(
      res.status,
      detail,
      `POST /api/claims/${claimId}/reply returned ${res.status}`,
    );
  }
  return (await res.json()) as ClaimActionResponse;
}

/**
 * Propagate the user's current profile name onto every claim they own
 * (POST /api/claims/sync-user-name). Each claim stores a COPY of the name
 * taken when it was created, which is what outbound emails use; this pushes
 * the latest name so replies / evidence follow-ups / retries stop using a
 * stale name. Called by the account-menu name editor right after it saves
 * the new name to Clerk. `token` is a Clerk session token from getToken().
 */
export async function syncDisplayName(
  token: string,
  name: string,
): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/api/claims/sync-user-name`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    throw new ClaimsApiError(
      res.status,
      null,
      `POST /api/claims/sync-user-name returned ${res.status}`,
    );
  }
}

/**
 * Re-run a held-back / failed-to-send claim from its stored fields
 * (POST /api/claims/{id}/retry). Reuses the submit pipeline and creates a
 * fresh claim. Valid only for validator_failed / send_failed; the API
 * returns 409 otherwise.
 */
export async function retryClaim(
  claimId: string,
  token: string,
): Promise<ClaimResponse> {
  const res = await fetch(`${apiBaseUrl()}/api/claims/${claimId}/retry`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ClaimsApiError(
      res.status,
      detail,
      `POST /api/claims/${claimId}/retry returned ${res.status}`,
    );
  }
  return (await res.json()) as ClaimResponse;
}

// ---------- Broadcast negotiation (Day 3-4) ---------------------------
//
// One click asks a GROUP of reachable shops at once; each shop is a
// normal claim sharing one broadcast_id. These wrap the /api/broadcasts
// routes and are consumed by the one-button AskPanel (createBroadcast)
// and the rebuilt requests page (listBroadcasts, acceptOffer).

/** One shop in a broadcast: the same claim body PLUS that shop's price
 * breakdown, snapshotted onto the claim for the requests TFP ledger. */
export interface BroadcastTarget extends ClaimRequestBody {
  /** Opaque per-shop True-Final-Price breakdown, snapshotted onto the
   * claim (price_components_snapshot) so the requests screen can later
   * rebuild the ledger with the agreed price. The panel passes the
   * comparison row's price_components through verbatim; the API layer
   * treats it as opaque JSON (the backend stores it as JSONB). */
  price_components?: unknown[];
}

export interface BroadcastShopResult {
  retailer_slug: string;
  status: string;
  claim_id?: string | null;
  transaction_id?: string | null;
  error?: unknown;
}

export interface BroadcastCreateResponse {
  broadcast_id: string;
  expires_at: string;
  window_hours: number;
  asked: number;
  results: BroadcastShopResult[];
}

export interface BroadcastGroup {
  broadcast_id: string;
  created_at?: string | null;
  expires_at?: string | null;
  product_name?: string | null;
  state: "collecting" | "choose" | "agreed" | "no_offers";
  asked: number;
  offers_in: number;
  accepted_claim_id?: string | null;
  /** Lowest AGREED price among offers; the requests screen rebuilds the
   * full True Final Price from each shop's price_components_snapshot. */
  best_offer_price_aud?: string | null;
  shops: ClaimListItem[];
}

export interface BroadcastListResponse {
  broadcasts: BroadcastGroup[];
}

export interface BroadcastAcceptResponse {
  ok: boolean;
  broadcast_id: string;
  accepted_claim_id: string;
  winner_notified: boolean;
  goodbyes_sent: number;
}

/** Fan a single click out to N reachable shops. */
export async function createBroadcast(
  targets: BroadcastTarget[],
  windowHours: number | undefined,
  token: string,
): Promise<BroadcastCreateResponse> {
  const res = await fetch(`${apiBaseUrl()}/api/broadcasts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ targets, window_hours: windowHours }),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ClaimsApiError(
      res.status,
      detail,
      `POST /api/broadcasts returned ${res.status}`,
    );
  }
  return (await res.json()) as BroadcastCreateResponse;
}

/** Owner-scoped list of the caller's broadcasts, grouped, newest first. */
export async function listBroadcasts(
  token: string,
): Promise<BroadcastGroup[]> {
  const res = await fetch(`${apiBaseUrl()}/api/broadcasts`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new ClaimsApiError(
      res.status,
      null,
      `GET /api/broadcasts returned ${res.status}`,
    );
  }
  const body = (await res.json()) as BroadcastListResponse;
  return body.broadcasts;
}

/** Take one shop's offer in a broadcast (records it + tells the shops). */
export async function acceptOffer(
  broadcastId: string,
  claimId: string,
  token: string,
): Promise<BroadcastAcceptResponse> {
  const res = await fetch(
    `${apiBaseUrl()}/api/broadcasts/${broadcastId}/accept`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ claim_id: claimId }),
    },
  );
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ClaimsApiError(
      res.status,
      detail,
      `POST /api/broadcasts/${broadcastId}/accept returned ${res.status}`,
    );
  }
  return (await res.json()) as BroadcastAcceptResponse;
}
